import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Typography,
  Box,
} from "@mui/material";

const formatReportDate = (value) => {
  const dateText = String(value).trim();
  if (!dateText) return "N/A";

  // Trip reports return day-first dates, which the Date constructor does not
  // parse consistently (for example, 29/09/2026).
  const dayFirstMatch = dateText.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[\s,T].*)?$/,
  );

  if (dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
    const isValidDate =
      parsedDate.getFullYear() === Number(year) &&
      parsedDate.getMonth() === Number(month) - 1 &&
      parsedDate.getDate() === Number(day);

    return isValidDate
      ? parsedDate.toLocaleDateString()
      : "N/A";
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime())
    ? "N/A"
    : parsedDate.toLocaleDateString();
};

const formatCellValue = (value, column) => {
  if (value === null || value === undefined) return "NA";

  switch (column.key) {
    case "schedules":
      if (Array.isArray(value)) {
        return value.length === 0
          ? "No Schedules"
          : value.map((s) => s.scheduleId || "Unknown").join(", ");
      }
      // If it's a string or something else, just return it (or "N/A" if empty)
      return value && value !== "N/A" ? value : "No Schedules";
    case "averageSpeed":
      const numValue = parseFloat(value);
      return isNaN(numValue) ? (
        "N/A"
      ) : numValue === 0 ? (
        <span
        //  style={{ color: "red" }}
         >{numValue} km/h</span>
      ) : (
        `${numValue.toFixed(2)} km/h`
      );
    case "date":
      return formatReportDate(value);
    case "time":
      // If value is already a time string like "15:15:22", just return it
      if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
      // Otherwise, try to parse as date
      return new Date(value).toLocaleTimeString();
    case "datetime":
      return new Date(value).toLocaleString();
    case "duration":
      return `${value}`;
    default:
      return value;
  }
};

const ReportTable = ({ reportData }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const parseCustomDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 0;

    try {
      const [datePart, timePart] = dateTimeStr.split(", ");
      const [day, month, year] = datePart.split("/");
      const [time, period] = timePart.split(" ");
      const [hours, minutes, seconds] = time.split(":");

      let hour = parseInt(hours, 10);
      if (period?.toLowerCase() === "pm" && hour < 12) hour += 12;
      if (period?.toLowerCase() === "am" && hour === 12) hour = 0;

      return new Date(year, month - 1, day, hour, minutes, seconds).getTime();
    } catch (e) {
      return 0;
    }
  };
  const sortedData = React.useMemo(() => {
    if (!reportData || !reportData.data || !reportData.columns) {
      return [];
    }

    if (!sortConfig.key) {
      return reportData.data;
    }

    return [...reportData.data].sort((a, b) => {
      let valueA = a[sortConfig.key];
      let valueB = b[sortConfig.key];
      console.log(sortConfig.key, valueA, valueB);
      if (sortConfig.key === "timestamp") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      } else if (sortConfig.key === "entryTime") {
        valueA = parseCustomDateTime(valueA);
        valueB = parseCustomDateTime(valueB);
      } else if (sortConfig.key === "exitTime ") {
        valueA = parseCustomDateTime(valueA);
        valueB = parseCustomDateTime(valueB);
      } else if (sortConfig.key === "averageSpeed") {
        valueA = parseFloat(valueA) || 0;
        valueB = parseFloat(valueB) || 0;
      } else if (sortConfig.key === "schedules") {
        valueA = Array.isArray(valueA) ? valueA.length : 0;
        valueB = Array.isArray(valueB) ? valueB.length : 0;
      } else {
        valueA = String(valueA || "").toLowerCase();
        valueB = String(valueB || "").toLowerCase();
      }

      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [reportData, sortConfig]);

  const handleSort = (columnKey) => {
    const column = reportData?.columns?.find((col) => col.key === columnKey);
    if (!column?.sortable) return;

    const direction =
      sortConfig.key === columnKey && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ key: columnKey, direction });
  };

  if (reportData?.sections?.length) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {reportData.sections.map((section, index) => (
          <Box key={section.sectionTitle || index}>
            <Typography
              sx={{
                color: "#183059",
                fontFamily: "Roboto",
                fontWeight: 700,
                fontSize: "16px",
                mb: 1,
              }}
            >
              {section.sectionTitle}
            </Typography>
            <ReportTable reportData={section} />
          </Box>
        ))}
      </Box>
    );
  }

  if (!reportData || !reportData.data || !reportData.columns) {
    return <Typography align="center">No Data Available</Typography>;
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: { xs: "25vh", sm: "35vh", md: "40vh" },
        overflowY: "auto",
        backgroundColor: "white",
        boxShadow: "none",
        border: "1px solid #ddd",
      }}
    >
      <Table size="small" sx={{ borderCollapse: "collapse" }}>
        <TableHead sx={{ backgroundColor: "#B7BFCC", borderRadius: "8px" }}>
          <TableRow>
            {reportData.columns.map((column) => (
              <TableCell
                key={column.key}
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#B7BFCC",
                  zIndex: 1,
                  fontWeight: "500",
                  fontSize: { xs: "10px", sm: "13px", md: "16px" },
                  border: "none",
                  borderBottom: "1px solid #ddd",
                  padding: { xs: "6px 8px", sm: "7px 10px", md: "8px 12px" },
                  color: "#333",
                  verticalAlign: "middle",
                }}
              >
                {column.sortable ? (
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Typography
                      component="span"
                      fontWeight="bold"
                      fontSize={"12px"}
                    >
                      {column.label}
                    </Typography>
                    <TableSortLabel
                      active={sortConfig.key === column.key}
                      direction={
                        sortConfig.key === column.key
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort(column.key)}
                      sx={{
                        marginLeft: "2px",
                        verticalAlign: "middle",
                        // Make the sort icon always visible
                        "& .MuiTableSortLabel-icon": {
                          opacity: sortConfig.key === column.key ? 1 : 0.5,
                          transition: "opacity 0.2s ease-in-out",
                        },
                        // Show icon even when not active
                        "&:not(.Mui-active) .MuiTableSortLabel-icon": {
                          opacity: 0.3,
                        },
                        // Override the default hover behavior to always show
                        "&.MuiTableSortLabel-root": {
                          "& .MuiTableSortLabel-icon": {
                            opacity: sortConfig.key === column.key ? 1 : 0.3,
                          },
                          "&:hover .MuiTableSortLabel-icon": {
                            opacity: 0.7,
                          },
                        },
                      }}
                      // Force the icon to always be visible
                      IconComponent={(props) => (
                        <span
                          {...props}
                          style={{
                            ...props.style,
                            opacity: 1,
                            fontSize: "10px",
                          }}
                        >
                          {sortConfig.key === column.key &&
                          sortConfig.direction === "desc"
                            ? "▼"
                            : "▲"}
                        </span>
                      )}
                    />
                  </span>
                ) : (
                  <Typography component="span" fontWeight="bold">
                    {column.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((row, idx) => (
              <TableRow
                key={row.deviceId || idx} // Use deviceId for unique key
                sx={{
                  backgroundColor: "white",
                  "&:hover": { backgroundColor: "#f9f9f9" },
                }}
              >
                {reportData.columns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      fontSize: { xs: "12px", sm: "14px", md: "14px" },
                      border: "none",
                      borderBottom: "1px solid #eee",
                      padding: {
                        xs: "6px 8px",
                        sm: "7px 10px",
                        md: "8px 12px",
                      },
                      color: "#555",
                    }}
                  >
                    {formatCellValue(row[column.key], column)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={reportData.columns.length}
                sx={{ textAlign: "center", padding: "16px", color: "#555" }}
              >
                No Data Available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReportTable;
